import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { energyApi } from '../api';
import { CircadianRhythmByDate } from '../types';

interface UseCircadianHeatmapParams {
  userId: string;
  calendarIds: string[];
  metricId: string;
  dates: string[];
  timezone?: string;
  enableStressCombineToEnergy?: boolean;
  forceEnabled?: boolean;
}

interface UseSleepDetailsQueryParams {
  queryKey: [
    'lifestack.circadian',
    { userId: string; calendarIds: string[]; metricId: string; date: string },
  ];
}

export const useCircadianHeatmap = ({
  userId,
  calendarIds,
  metricId,
  dates,
  timezone = dayjs.tz.guess(),
  enableStressCombineToEnergy = false,
  forceEnabled = false,
}: UseCircadianHeatmapParams) => {
  const uniqueDates = useMemo(() => Array.from(new Set(dates)), [dates]);

  const circadianQueries = useQueries({
    // @ts-ignore
    queries: uniqueDates.map((date) => ({
      queryKey: ['lifestack.circadian', { userId, calendarIds, metricId, date }],
      queryFn: ({ queryKey }: UseSleepDetailsQueryParams) => {
        const [, { userId, calendarIds, metricId, date }] = queryKey;
        return energyApi
          .getCircadian(userId, calendarIds, metricId, date, timezone, enableStressCombineToEnergy)
          .then(
            ({ data }) =>
              ({
                date,
                values: data.circadian,
                fluctuations: data.fluctuations,
              }) as CircadianRhythmByDate
          );
      },
      refetchOnWindowFocus: false,
      enabled: forceEnabled || (!!metricId && !!userId && !!date),
      staleTime: 15000, // 15s
    })),
  });

  return {
    circadianRhythmsByDate: circadianQueries
      .map((query) => query.data ?? [])
      .flat() as CircadianRhythmByDate[],
    isLoading: circadianQueries.some((query) => query.isLoading),
    ...circadianQueries,
  };
};
